import { useEffect, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    MenuItem,
    TextField,
} from '@mui/material';

import { IncidentSummary, useCloseIncident } from '../../api/incidentLog';

/**
 * "Close this incident?" — with the closer's reason (shown wherever the
 * incident is afterwards) and an optional commander stand-down.
 */
export default function CloseIncidentDialog({
    projectId,
    incident,
    hasAgent,
    onClose,
    onClosed,
}: {
    projectId: string | number | undefined;
    /** The incident being closed; null hides the dialog. */
    incident: IncidentSummary | null;
    /** An AI agent is monitoring it (it will be stopped). */
    hasAgent: boolean;
    onClose: () => void;
    onClosed?: () => void;
}) {
    const closeIncident = useCloseIncident(projectId);
    const [quietHours, setQuietHours] = useState(0);
    const [reason, setReason] = useState('');

    useEffect(() => {
        if (incident) {
            setQuietHours(0);
            setReason('');
        }
    }, [incident?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleConfirm = () => {
        if (!incident) return;
        closeIncident.mutate(
            { incidentId: incident.id, quietHours: quietHours || undefined, reason },
            {
                onSuccess: () => onClosed?.(),
                onSettled: () => onClose(),
            }
        );
    };

    return (
        <Dialog open={incident !== null} onClose={onClose} onClick={(e) => e.stopPropagation()}>
            <DialogTitle>Close incident #{incident?.id}?</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    It leaves the open-incident count and stops collecting detections
                    {hasAgent ? ', and the AI agent monitoring it is stopped' : ''}. New
                    detections inside the fire&apos;s detection footprint reopen it silently
                    as a linked rekindle; spread beyond it (or an agency warning) alerts
                    subscribers again.
                </DialogContentText>
                <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    sx={{ mt: 2 }}
                    label="Reason (shown on the incident)"
                    placeholder="e.g. Contained by RFS crews overnight; residual heat only."
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    inputProps={{ maxLength: 2000 }}
                />
                <TextField
                    select
                    fullWidth
                    size="small"
                    sx={{ mt: 2 }}
                    label="Stand down re-detections"
                    value={quietHours}
                    onChange={(e) => setQuietHours(Number(e.target.value))}
                    helperText="During a stand-down, new detections here stay silent regardless of position — only a Watch and Act or Emergency Warning escalates."
                >
                    <MenuItem value={0}>No stand-down</MenuItem>
                    <MenuItem value={6}>6 hours</MenuItem>
                    <MenuItem value={12}>12 hours</MenuItem>
                    <MenuItem value={24}>24 hours</MenuItem>
                    <MenuItem value={48}>48 hours</MenuItem>
                </TextField>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleConfirm}
                    disabled={closeIncident.isPending}
                >
                    {closeIncident.isPending ? 'Closing…' : 'Close incident'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
